import React, { FC, useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { ProblemsTable } from "common/components/ProblemsTable";
import { useShipmentProblems } from "./useShipmentProblems";
import { DefaultTheme } from "common/components/ui";
import styled from "@emotion/styled";
import { useProblemsTableColumns } from "common/components/ProblemsTable/useProblemsTableColumns";
import { ShipmentProblemBlade } from "./blades/ShipmentProblemBlade";
import { ShipmentDetailCard, ShipmentDetailCardProps } from "inbounds/steps/ship/view/cards/ShipmentDetailCard";
import { useStorageAppointmentDetails } from "../StorageShipmentConfirmation/useStorageAppointmentDetails";
import { ShipmentProblemsTable } from "inbounds/steps/ship/view/cards/ShipmentProblems/components/ShipmentProblemsTable";

const StyledShipmentDetailCard = styled(ShipmentDetailCard)<ShipmentDetailCardProps, DefaultTheme>(
  ({ theme }) => `
  th:first-of-type, td:first-of-type {
    padding-left: ${theme.spacing.S5};
  }
`
);

const TabContainer = styled.div<{ theme: DefaultTheme }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.S6};
  padding: ${({ theme }) => `${theme.spacing.S4}  0`};
  margin-top: ${({ theme }) => theme.spacing.S4};
`;

const Tab = styled.div<{ active: boolean; theme: DefaultTheme }>`
  padding: ${({ theme }) => theme.spacing.S2} 0;
  cursor: pointer;
  color: ${({ active, theme }) => (active ? theme.colors.NEUTRAL[900] : theme.colors.NEUTRAL[400])};
  border-bottom: 2px solid ${({ active, theme }) => (active ? theme.colors.BLUE[400] : "transparent")};
  font-weight: ${({ active, theme }) => (active ? theme.font.weight.BOLD : theme.font.weight.REGULAR)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.S2};
`;

const ProblemBadge = styled.div<{ theme: DefaultTheme }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 2px 4px;
  min-width: 15px;
  height: 20px;
  background: ${({ theme }) => theme.colors.RED[300]};
  border-radius: ${({ theme }) => theme.border.radius.R2};
  color: #ffffff;
  font-size: ${({ theme }) => theme.font.size.F1};
  text-align: center;
  font-weight: ${({ theme }) => theme.font.weight.BOLD};
`;

const ProblemTypeHeader = styled.div<{ theme: DefaultTheme }>(
  ({ theme }) => `
  font-family: ${theme.font.family.STANDARD};
  font-weight: ${theme.font.weight.BOLD};
  font-size: ${theme.font.size.F3};
  line-height: 24px;
  margin-top: ${theme.spacing.S4};
  color: ${theme.colors.NEUTRAL[500]};
`
);

const StyledHeader = styled.div<{ theme: DefaultTheme }>(
  ({ theme }) => `
  font-family: ${theme.font.family.TITLE};
  font-weight: ${theme.font.weight.BOLD};
  font-size: ${theme.font.size.F3};
  line-height: 24px;
  letter-spacing: 0.7px;
  color: ${theme.colors.NEUTRAL[500]};
`
);

/** Do not show this card if there are no shipment problems */
export const ShipmentProblemsCard: FC = () => {
  const [activeTab, setActiveTab] = useState<"inventoryProblem" | "shipmentProblems">("inventoryProblem");
  const { rows, onRowClick, isDataLoading, selectedProblemCase } = useShipmentProblems();
  const { columns } = useProblemsTableColumns();
  const {
    quantity,
    verifiedQuantity,
    checkedOutAt,
    isLoading: isAppointmentLoading,
    error: appointmentError,
  } = useStorageAppointmentDetails();

  // Status indicators
  const isLoading = isDataLoading || isAppointmentLoading;
  const hasError = !!appointmentError;

  const hasInventoryProblems = rows.length > 0;
  const inventoryProblemCount = rows.length;

  // Calculate if there's a pallet discrepancy - only if appointment data is available
  const hasPalletDiscrepancy =
    !hasError && !isLoading && !!quantity && !!verifiedQuantity && quantity !== verifiedQuantity;

  // Determine if there are any shipment problems
  const hasShipmentProblems = hasPalletDiscrepancy;

  // Count shipment problems
  const shipmentProblemCount = hasPalletDiscrepancy ? 1 : 0;

  // Only show the card when all data is loaded AND we have problems to display
  const shouldShowCard = !isLoading && (hasInventoryProblems || hasShipmentProblems);

  // If there are no inventory problems but there are shipment problems,
  // automatically show the shipmentProblems tab
  useEffect(() => {
    if (!hasInventoryProblems && hasShipmentProblems) {
      setActiveTab("shipmentProblems");
    }
  }, [hasInventoryProblems, hasShipmentProblems]);

  // If API fails, don't render the card
  if (hasError) {
    return null;
  }

  if (!shouldShowCard) {
    return null;
  }

  return (
    <>
      <ShipmentProblemBlade shipmentProblem={selectedProblemCase} />
      <StyledShipmentDetailCard
        header={
          <StyledHeader>
            <FormattedMessage id="inbounds.viewShipment.shipmentProblemsCard.title" defaultMessage="Problems" />
          </StyledHeader>
        }
      >
        {/* Show tabs only if BOTH problem types exist */}
        {hasInventoryProblems && hasShipmentProblems && (
          <TabContainer>
            <Tab active={activeTab === "inventoryProblem"} onClick={() => setActiveTab("inventoryProblem")}>
              <FormattedMessage id="problems.tab.inventoryProblem" defaultMessage="Inventory problems" />
              {inventoryProblemCount > 0 && <ProblemBadge>{inventoryProblemCount}</ProblemBadge>}
            </Tab>
            <Tab active={activeTab === "shipmentProblems"} onClick={() => setActiveTab("shipmentProblems")}>
              <FormattedMessage id="problems.tab.shipmentProblems" defaultMessage="Shipment problems" />
              {shipmentProblemCount > 0 && <ProblemBadge>{shipmentProblemCount}</ProblemBadge>}
            </Tab>
          </TabContainer>
        )}

        {/* Show headers based on which problem type exists or which tab is active */}
        {hasInventoryProblems && hasShipmentProblems ? (
          /* When both exist, show header based on active tab */
          activeTab === "inventoryProblem" ? (
            <ProblemTypeHeader>
              <FormattedMessage id="problems.tab.inventoryProblem" defaultMessage="Inventory problems" />
            </ProblemTypeHeader>
          ) : (
            <ProblemTypeHeader>
              <FormattedMessage id="problems.tab.shipmentProblems" defaultMessage="Shipment problems" />
            </ProblemTypeHeader>
          )
        ) : (
          /* When only one exists, show its header */
          <>
            {hasInventoryProblems && (
              <ProblemTypeHeader>
                <FormattedMessage id="problems.tab.inventoryProblem" defaultMessage="Inventory problems" />
              </ProblemTypeHeader>
            )}
            {hasShipmentProblems && (
              <ProblemTypeHeader>
                <FormattedMessage id="problems.tab.shipmentProblems" defaultMessage="Shipment problems" />
              </ProblemTypeHeader>
            )}
          </>
        )}

        {/* Show content based on available problem types and active tab */}
        {(hasInventoryProblems && !hasShipmentProblems) ||
        (hasInventoryProblems && hasShipmentProblems && activeTab === "inventoryProblem") ? (
          <ProblemsTable columns={columns} rows={rows} onRowClick={onRowClick} isDataLoading={isDataLoading} />
        ) : null}

        {/* Show shipment problems table when appropriate */}
        {hasShipmentProblems && (!hasInventoryProblems || activeTab === "shipmentProblems") && (
          <ShipmentProblemsTable
            palletQuantity={quantity ?? 0}
            palletVerifiedQuantity={verifiedQuantity ?? 0}
            checkedOutAt={checkedOutAt ?? ""}
            isDataLoading={isLoading}
          />
        )}
      </StyledShipmentDetailCard>
    </>
  );
};
